// DesignExperiments.tsx - Preview all three design variations
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, BookOpen, Music } from 'lucide-react';
import './ClassicLiturgical.css';
import './ModernSacred.css';
import './EarthParchment.css';

// Sample data for the preview - includes multiple seasons to show color treatments
const sampleServices = [
  {
    id: 1,
    date: 'December 15, 2024',
    name: 'Third Sunday of Advent',
    time: '10:00 AM',
    season: 'advent',
    readings: {
      ot: 'Isaiah 35:1-10',
      psalm: 'Psalm 146:5-10',
      epistle: 'James 5:7-10',
      gospel: 'Matthew 11:2-11'
    },
    songs: [
      { placement: 'Entrance', title: 'O Come, O Come, Emmanuel', composer: 'Traditional' },
      { placement: 'Offertory', title: 'Lo, How a Rose E\'er Blooming', composer: 'Praetorius' },
      { placement: 'Communion', title: 'Wait for the Lord', composer: 'Taizé' },
      { placement: 'Sending', title: 'People, Look East', composer: 'Besançon Carol' }
    ]
  },
  {
    id: 2,
    date: 'December 25, 2024',
    name: 'The Nativity of Our Lord',
    time: '10:00 AM',
    season: 'christmas',
    readings: {
      ot: 'Isaiah 52:7-10',
      psalm: 'Psalm 98',
      epistle: 'Hebrews 1:1-12',
      gospel: 'John 1:1-14'
    },
    songs: [
      { placement: 'Entrance', title: 'O Come, All Ye Faithful', composer: 'Wade' },
      { placement: 'Offertory', title: 'What Child Is This', composer: 'Traditional' },
      { placement: 'Communion', title: 'Silent Night', composer: 'Gruber' },
      { placement: 'Sending', title: 'Joy to the World', composer: 'Handel' }
    ]
  },
  {
    id: 3,
    date: 'March 9, 2025',
    name: 'First Sunday in Lent',
    time: '10:00 AM',
    season: 'lent',
    readings: {
      ot: 'Genesis 9:8-17',
      psalm: 'Psalm 25:1-9',
      epistle: '1 Peter 3:18-22',
      gospel: 'Mark 1:9-15'
    },
    songs: [
      { placement: 'Entrance', title: 'Lord, Who Throughout These Forty Days', composer: 'Hernaman' },
      { placement: 'Offertory', title: 'Create in Me a Clean Heart', composer: 'Psalm 51' },
      { placement: 'Communion', title: 'Jesus, Remember Me', composer: 'Taizé' },
      { placement: 'Sending', title: 'Guide Me, O Thou Great Jehovah', composer: 'Hughes' }
    ]
  },
  {
    id: 4,
    date: 'June 8, 2025',
    name: 'Second Sunday after Pentecost',
    time: '10:00 AM',
    season: 'ordinary',
    readings: {
      ot: '1 Samuel 8:4-20',
      psalm: 'Psalm 138',
      epistle: '2 Corinthians 4:13-5:1',
      gospel: 'Mark 3:20-35'
    },
    songs: [
      { placement: 'Entrance', title: 'Joyful, Joyful, We Adore Thee', composer: 'Beethoven' },
      { placement: 'Offertory', title: 'We Are Called', composer: 'Haas' },
      { placement: 'Communion', title: 'One Bread, One Body', composer: 'Foley' },
      { placement: 'Sending', title: 'Sent Forth by God\'s Blessing', composer: 'Welsh' }
    ]
  }
];

type ThemeType = 'classic' | 'modern' | 'earth';

const themes: { id: ThemeType; name: string; description: string }[] = [
  { id: 'classic', name: 'Classic Liturgical', description: 'Burgundy, navy, cream with gold accents' },
  { id: 'modern', name: 'Modern Sacred', description: 'Warm neutrals with refined liturgical touches' },
  { id: 'earth', name: 'Earth & Parchment', description: 'Terracotta, forest green, manuscript feel' }
];

// Classic Liturgical Component
const ClassicLiturgicalPreview: React.FC = () => (
  <div className="classic-liturgical">
    <div className="page-header">
      <h1 className="page-title">Church Music Planning</h1>
      <p className="page-subtitle">Preparing hearts for worship through sacred song</p>
    </div>

    <div className="year-nav">
      <button className="year-nav-btn"><ChevronLeft size={18} /></button>
      <div className="year-display">
        <div className="year-main">Year B 2024-2025</div>
        <div className="year-sub">Advent through Christ the King</div>
      </div>
      <button className="year-nav-btn"><ChevronRight size={18} /></button>
    </div>

    <div className="content-wrapper">
      {sampleServices.map(service => (
        <div key={service.id} className="service-card">
          <div className={`service-card-header ${service.season}`}>
            <div className="service-date">{service.date}</div>
            <div className="service-name">{service.name}</div>
            <div className="service-time">{service.time}</div>
          </div>
          <div className="service-card-body">
            <div className="readings-section">
              <h4 className="section-title"><BookOpen size={18} /> Readings</h4>
              <div className="reading-item">
                <div className="reading-label">Old Testament</div>
                <div className="reading-reference">{service.readings.ot}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">Psalm</div>
                <div className="reading-reference">{service.readings.psalm}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">Epistle</div>
                <div className="reading-reference">{service.readings.epistle}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">Gospel</div>
                <div className="reading-reference">{service.readings.gospel}</div>
              </div>
            </div>
            <div className="songs-section">
              <h4 className="section-title"><Music size={18} /> Music Selections</h4>
              {service.songs.map((song, idx) => (
                <div key={idx} className="song-item">
                  <div className="song-placement">{song.placement}</div>
                  <div className="song-details">
                    <div className="song-title">{song.title}</div>
                    <div className="song-composer">{song.composer}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Modern Sacred Component
const ModernSacredPreview: React.FC = () => (
  <div className="modern-sacred">
    <div className="page-header">
      <h1 className="page-title">Music Planning</h1>
      <p className="page-subtitle">St. Luke's Episcopal Church</p>
    </div>

    <div className="year-nav">
      <button className="year-nav-btn"><ChevronLeft size={18} /></button>
      <div className="year-display">
        <div className="year-main">2024-2025</div>
        <div className="year-sub">Year B</div>
      </div>
      <button className="year-nav-btn"><ChevronRight size={18} /></button>
    </div>

    <div className="content-wrapper">
      {sampleServices.map(service => (
        <div key={service.id} className="service-card">
          <div className={`service-card-header ${service.season}`}>
            <div className="header-left">
              <div className="service-date">{service.date}</div>
              <div className="service-name">{service.name}</div>
            </div>
            <div className="service-time">{service.time}</div>
          </div>
          <div className="service-card-body">
            <div className="readings-section">
              <h4 className="section-title">READINGS</h4>
              <div className="reading-item">
                <div className="reading-label">OT</div>
                <div className="reading-reference">{service.readings.ot}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">Psalm</div>
                <div className="reading-reference">{service.readings.psalm}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">NT</div>
                <div className="reading-reference">{service.readings.epistle}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">Gospel</div>
                <div className="reading-reference">{service.readings.gospel}</div>
              </div>
            </div>
            <div className="songs-section">
              <h4 className="section-title">MUSIC</h4>
              {service.songs.map((song, idx) => (
                <div key={idx} className="song-item">
                  <div className="song-placement">{song.placement}</div>
                  <div className="song-details">
                    <div className="song-title">{song.title}</div>
                    <div className="song-composer">{song.composer}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Earth & Parchment Component
const EarthParchmentPreview: React.FC = () => (
  <div className="earth-parchment">
    <div className="page-header">
      <h1 className="page-title">Liturgical Music</h1>
      <p className="page-subtitle">Planning for the Church Year</p>
    </div>

    <div className="year-nav">
      <button className="year-nav-btn"><ChevronLeft size={18} /></button>
      <div className="year-display">
        <div className="year-main">Year B 2024-2025</div>
        <div className="year-sub">Lectionary Cycle</div>
      </div>
      <button className="year-nav-btn"><ChevronRight size={18} /></button>
    </div>

    <div className="content-wrapper">
      {sampleServices.map(service => (
        <div key={service.id} className="service-card">
          <div className={`service-card-header ${service.season}`}>
            <div className={`season-badge ${service.season}`}>
              {service.season.charAt(0).toUpperCase()}
            </div>
            <div className="header-info">
              <div className="service-date">{service.date}</div>
              <div className="service-name">{service.name}</div>
            </div>
            <div className="service-time">{service.time}</div>
          </div>
          <div className="service-card-body">
            <div className="readings-section">
              <h4 className="section-title">Lectionary Readings</h4>
              <div className="reading-item">
                <div className="reading-label">Old Testament</div>
                <div className="reading-reference">{service.readings.ot}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">Psalm</div>
                <div className="reading-reference">{service.readings.psalm}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">Epistle</div>
                <div className="reading-reference">{service.readings.epistle}</div>
              </div>
              <div className="reading-item">
                <div className="reading-label">Gospel</div>
                <div className="reading-reference">{service.readings.gospel}</div>
              </div>
            </div>
            <div className="songs-section">
              <h4 className="section-title">Music Selections</h4>
              {service.songs.map((song, idx) => (
                <div key={idx} className="song-item">
                  <div className="song-placement">{song.placement}</div>
                  <div className="song-details">
                    <div className="song-title">{song.title}</div>
                    <div className="song-composer">{song.composer}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Main Experiments Page
const DesignExperiments: React.FC = () => {
  const [activeTheme, setActiveTheme] = useState<ThemeType>('classic');

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Theme Selector - Fixed at top */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: '#1a1a1a',
        padding: '1rem 2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
      }}>
        <span style={{ color: '#888', fontSize: '0.85rem', marginRight: '0.5rem' }}>
          DESIGN PREVIEW:
        </span>
        {themes.map(theme => (
          <button
            key={theme.id}
            onClick={() => setActiveTheme(theme.id)}
            style={{
              padding: '0.5rem 1rem',
              border: activeTheme === theme.id ? '2px solid #C9A227' : '1px solid #444',
              background: activeTheme === theme.id ? '#2a2a2a' : 'transparent',
              color: activeTheme === theme.id ? '#C9A227' : '#aaa',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 500,
              transition: 'all 0.2s ease'
            }}
          >
            {theme.name}
          </button>
        ))}
      </div>

      {/* Theme Description */}
      <div style={{
        background: '#f5f5f5',
        padding: '0.75rem 2rem',
        textAlign: 'center',
        borderBottom: '1px solid #ddd'
      }}>
        <strong>{themes.find(t => t.id === activeTheme)?.name}</strong>
        <span style={{ color: '#666', marginLeft: '0.75rem' }}>
          {themes.find(t => t.id === activeTheme)?.description}
        </span>
      </div>

      {/* Theme Preview */}
      {activeTheme === 'classic' && <ClassicLiturgicalPreview />}
      {activeTheme === 'modern' && <ModernSacredPreview />}
      {activeTheme === 'earth' && <EarthParchmentPreview />}
    </div>
  );
};

export default DesignExperiments;
